import { api } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

const toLocalInput = (ts) => {
   const d = new Date(ts);
   const pad = (n) => String(n).padStart(2, "0");
   return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/*
   datetime-local -> a real instant, for sending.

   THE BUG THIS FIXES (2026-08-24): a datetime-local input yields a bare
   "2026-09-01T08:00" with NO timezone. Sent as-is, Postgres reads it in the
   SERVER's zone - Railway runs UTC - so 8 AM Arizona was stored as 8 AM UTC,
   which is 1 AM Arizona. Reading it back converted UTC to local and showed
   01:00. Every time was landing seven hours early.

   `new Date(s)` on a datetime-local string parses it as LOCAL time (that is what
   the HTML spec says the control means), so toISOString() produces the correct
   absolute instant with a Z on it, and Postgres has nothing left to guess.

   ⚠️ This bug was NOT introduced by the open/close work - due_at has always sent
   its value the same way, so any due date already saved is stored seven hours
   early and wants re-entering.
*/
const localToIso = (v) => {
   if (!v) return null;
   const d = new Date(v);
   return isNaN(d.getTime()) ? null : d.toISOString();
};

// "Sep 1, 8:00 AM" - short enough to sit on a card without wrapping.
const shortWhen = (ts) => new Date(ts).toLocaleString(undefined, {
   month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});

/*
   The availability line on an assignment card.

   Renders NOTHING when there is no window, so a class that never uses one looks
   exactly as it did. When there is a window it states which of the three states
   the assignment is in RIGHT NOW - not just the dates - because "opens Sep 1"
   read on Sep 3 still leaves you working out whether that already happened.
*/
function windowLine(a) {
   if (!a.open_at && !a.close_at) return "";
   const now = Date.now();
   const opens = a.open_at ? new Date(a.open_at).getTime() : null;
   const closes = a.close_at ? new Date(a.close_at).getTime() : null;

   let cls = "cd-window open", text;
   if (opens && now < opens) {
      cls = "cd-window pending";
      text = `Hidden from students until ${shortWhen(a.open_at)}`;
   } else if (closes && now >= closes) {
      cls = "cd-window closed";
      text = `Closed ${shortWhen(a.close_at)} - visible, not downloadable`;
   } else if (closes) {
      text = `Open to students, closes ${shortWhen(a.close_at)}`;
   } else {
      text = `Open to students since ${shortWhen(a.open_at)}`;
   }
   return `<div class="${cls}">${esc(text)}</div>`;
}

/*
   The mode line on an assignment card.

   Renders NOTHING for a plain whole/untimed deployment, so the overwhelming
   majority of cards look exactly as they did. Same principle as windowLine
   above: say something only when there is something to say.

   ⚠️ Worth having on the CARD rather than only inside the edit dialog. A timed,
   split-delivery assignment behaves very differently from its neighbours, and
   the only way to tell them apart used to be opening each one in turn - which
   is exactly how the wrong assignment gets set to on_start.
*/
function modeLine(a) {
   const bits = [];
   if (a.timing_mode === "async") {
      const mins = a.duration_secs ? Math.round(a.duration_secs / 60) : null;
      bits.push(mins ? `Timed ${mins} min` : "Timed");
      if (a.max_attempts) bits.push(`${a.max_attempts} attempt${a.max_attempts === 1 ? "" : "s"}`);
   }
   if (a.delivery_mode === "on_start") bits.push("revealed at Start");
   if (a.delivery_mode === "hidden") bits.push("hidden until sign-in");
   // A timed pack (2026-10-14) carries its own clock and grade rules, set on its
   // Rules page rather than in the edit dialog, so the card says what it is.
   if (a.archetype === "timed_pack") bits.unshift("Timed pack, tab by tab");
   return bits.length ? ` · <span class="cd-mode">${esc(bits.join(", "))}</span>` : "";
}

class AppClassDetail extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="class-detail">
            <div class="page-head">
               <button type="button" id="goback-btn" onclick="location.hash = '#classes'">← My classes</button>
               <h1 id="cd-title">Loading...</h1>
               <p class="sub" id="cd-sub"></p>
            </div>
            <div class="cd-visibility" id="cd-visibility" hidden>
               <div class="cd-vis-head">
                  <h2>What students in this class can see</h2>
                  <p class="cd-vis-sub">
                     Off by default. When a board is on, classmates appear by their
                     nickname, or as “Student 42” until they set one. Real names and
                     email addresses are never shown to students.
                  </p>
               </div>
               <label class="cd-vis-row">
                  <input type="checkbox" id="vis-scoreboard">
                  <span>
                     <strong>Class scoreboard</strong>
                     <em>Students can open the score and speed boards for this class's assignments.</em>
                  </span>
               </label>
               <label class="cd-vis-row">
                  <input type="checkbox" id="vis-engagement">
                  <span>
                     <strong>Class active time</strong>
                     <em>Students can see everyone's active minutes for the week and the term, ranked.</em>
                  </span>
               </label>
               <label class="cd-vis-row">
                  <input type="checkbox" id="vis-log-offline">
                  <span>
                     <strong>Students can log offline practice</strong>
                     <em>
                        Adds a form where students record a per-day total for practice
                        the add-in cannot see: a competition on another platform, a
                        walkthrough video, a case worked in a plain workbook. Each kind
                        of practice is its own entry, so one day can hold several. They
                        can fill in this week and last week, up to 4 hours a day across
                        everything they log for that day. Logged
                        minutes are self-reported, they count toward the weekly total
                        and the class board, and they are always shown separately from
                        measured time so you can see the split. Leave this off for a
                        graded class.
                     </em>
                  </span>
               </label>
               <label class="cd-vis-row">
                  <input type="checkbox" id="vis-game-launcher">
                  <span>
                     <strong>Game Launcher in the add-in</strong>
                     <em>
                        Students in this class get a Game Launcher button in Grid Professor
                        when they open it in a blank workbook, listing this class's timed
                        packs. Off by default; turn it on only for classes that should play
                        library packs on their own.
                     </em>
                  </span>
               </label>
               <p class="msg" id="cd-vis-msg"></p>

               <div class="cd-targets">
                  <h2>Active time targets</h2>
                  <p class="cd-vis-sub">
                     What this class's students are measured against on their My time
                     page. Leave both blank if this class has no active-time
                     requirement, and they will see their minutes with no progress
                     bar rather than a bar against a number nobody set.
                  </p>
                  <div class="cd-target-row">
                     <label>Weekly minimum
                        <input type="number" id="tgt-weekly" min="1" max="100800" step="1" placeholder="none">
                        <em>minutes per Mon-Sun week</em>
                     </label>
                     <label>Term target
                        <input type="number" id="tgt-cumulative" min="1" max="100800" step="1" placeholder="none">
                        <em>minutes across the term</em>
                     </label>
                     <button class="btn-secondary" id="tgt-save">Save targets</button>
                  </div>
                  <p class="msg" id="cd-tgt-msg"></p>
               </div>
            </div>

            <div class="cd-body">
               <div class="cd-section">
                  <div class="cd-section-head">
                     <h2>Roster</h2>
                     <button class="btn-primary" id="btn-new-student">+ Add Students</button>
                  </div>
                  <div id="cd-roster" class="cd-panel"></div>
               </div>
               <div class="cd-section">
                  <div class="cd-section-head">
                     <h2>Assignments</h2>
                     <button class="btn-primary" id="btn-new-assignment">+ New Assignment</button>
                  </div>
                  <div id="cd-assignments" class="cd-panel"></div>
               </div>
            </div>
         </section>

         <dialog class="class-dialog" id="new-student-dialog">
            <div class="student-dialog-inner">
               <h2>New student</h2>

               <div class="seg-tabs" role="tablist">
                  <button type="button" class="seg-tab active" data-mode="single" role="tab">Type or paste</button>
                  <button type="button" class="seg-tab" data-mode="bulk" role="tab">Import list</button>
               </div>

               <div class="student-panes">

                  <!-- SINGLE -->
                  <form method="dialog" id="new-student-form" class="student-pane" data-pane="single">
                     <label>Email addresses
                        <textarea name="email" rows="6" autocomplete="off" placeholder="student@arizona.edu&#10;another@arizona.edu&#10;a.third@arizona.edu"></textarea>
                     </label>
                     <p class="dropzone-hint">One or many. Separate by new lines, commas, or spaces. Anything that is not an email address is ignored, so pasting a column straight out of a roster works.</p>
                     <div class="dialog-actions">
                        <p class="msg" id="add-student-msg"></p>
                        <div class="dialog-buttons">
                           <button type="submit" class="btn-primary" id="btn-add-student">Add</button>
                           <button type="button" class="btn-secondary" id="btn-cancel-student">Cancel</button>
                        </div>
                     </div>
                  </form>

                  <!-- BULK -->
                  <form method="dialog" id="import-student-form" class="student-pane hidden" data-pane="bulk">
                     <div class="dropzone" id="student-dropzone">
                        <input type="file" id="student-list-file" accept=".csv,.xlsx,.xls,.xlsm" hidden>
                        <div class="dropzone-icon">⬆</div>
                        <p class="dropzone-text">Drop a CSV or Excel file, or <span class="link">browse</span></p>
                        <p class="dropzone-hint">One email per row. A column named "email" is used if present.</p>
                     </div>
                     <div class="dialog-actions">
                        <p class="msg" id="import-msg"></p>
                        <div class="dialog-buttons">
                           <button type="submit" class="btn-primary" id="btn-import">Import</button>
                           <button type="button" class="btn-secondary" id="btn-cancel-import">Cancel</button>
                        </div>
                     </div>
                  </form>
               </div>
            </div>
         </dialog>

         <dialog class="class-dialog" id="new-assignment-dialog">
            <form method="dialog" id="new-assignment-form">
               <h2>New assignment</h2>
               <label>Assignment
                  <div id="assignment-list" class="pick-list"></div>
                  <p class="pick-hint" id="assignment-multi-hint" hidden></p>
                  <input type="hidden" name="assignment_id" id="assignment-id">
               </label>
               <label>Opens
                  <input name="open_at" type="datetime-local">
               </label>
               <p class="field-hint">
                  Students cannot see this assignment at all until then. Leave
                  blank to make it available immediately.
               </p>
               <label>Closes (optional)
                  <input name="close_at" type="datetime-local">
               </label>
               <p class="field-hint">
                  After this they can still see it and their scores, but can no
                  longer download the file. Leave blank and it never closes.
               </p>
               <label>Due
                  <input name="due_at" type="datetime-local">
               </label>
               <label>Team formation
                  <select name="formation_method" id="formation-method">
                     <option value="individual">Individual (default)</option>
                     <option value="student_select">Teams — students pick (join code)</option>
                     <option value="random" disabled>Teams — random groups (coming soon)</option>
                     <option value="instructor_assigned" disabled>Teams — instructor-assigned (coming soon)</option>
                  </select>
               </label>
               <label id="group-size-label" style="display:none;">Target group size
                  <input name="target_group_size" type="number" min="1" value="3">
               </label>

               <!--
                  DELIVERY AND TIMING (2026-09-07).

                  Two SEPARATE controls, not one "async timed" mode, because they
                  answer different questions and the useful combinations cross:
                  deliver-on-start with no clock is "do not peek until you are
                  ready", and a clock with whole delivery is the simplest timed
                  assignment. Collapsing them would also foreclose the generic-
                  shell work later. See the design doc, section 1.

                  Sits with Opens / Closes / Due because all four live on the same
                  class_assignment row and all four answer "how does THIS class
                  get this assignment".
               -->
               <label>Delivery
                  <select name="delivery_mode" id="delivery-mode">
                     <option value="whole">Whole file (default)</option>
                     <option value="hidden">Tabs hidden until they sign in</option>
                     <option value="on_start">Cover page only, rest appears at Start</option>
                  </select>
               </label>
               <p class="field-hint" id="delivery-hint">
                  With "tabs hidden" they download the whole file but see only a
                  Start Here page; the tabs appear on their own once they sign in.
                  A student whose add-in will not load can still unhide them by
                  hand, and the cover tells them that nothing is recorded if they
                  do, so it is a reminder to sign in, not a lock. Good for
                  homework.<br>
                  With "cover page only" the download is a small starter file. The
                  assignment itself is added to their workbook when they press
                  Start in the add-in, so nobody can see it beforehand. Use this
                  for tests.
               </p>

               <label>Timing
                  <select name="timing_mode" id="timing-mode">
                     <option value="untimed">Untimed (default)</option>
                     <option value="async">Timed, each student starts their own clock</option>
                  </select>
               </label>
               <p class="field-hint">
                  The clock is on the server and keeps running once started, even
                  if the student closes Excel. Their score stops counting when it
                  expires or when they press Submit and finish.
               </p>

               <label id="duration-label" style="display:none;">Minutes
                  <input name="duration_mins" type="number" min="1" step="1" value="30">
               </label>
               <label id="attempts-label" style="display:none;">Attempts (blank = unlimited)
                  <input name="max_attempts" type="number" min="1" step="1" placeholder="unlimited">
               </label>

               <div class="dialog-actions">
                  <p class="msg err" id="new-assignment-error"></p>
                  <div class="dialog-buttons">
                     <button type="submit" class="btn-primary" id="btn-add-assignment">Add to Class</button>
                     <button type="button" class="btn-secondary" id="btn-cancel-assignment">Cancel</button>
                  </div>
               </div>
            </form>
         </dialog>
      `;

      // Button: add student individually
      this.studentDialog = this.querySelector("#new-student-dialog");
      this.querySelector("#btn-new-student").addEventListener("click", async () => {
         this.resetStudentDialog();
         this.studentDialog.showModal();
         document.activeElement?.blur();
      });
      this.querySelectorAll(".seg-tab").forEach((tab) => {
         tab.addEventListener("click", () => {
            const mode = tab.dataset.mode;
            this.querySelectorAll(".seg-tab").forEach((t) => t.classList.toggle("active", t === tab));
            this.querySelectorAll(".student-pane").forEach((p) => p.classList.toggle("hidden", p.dataset.pane !== mode));
         });
      });

      this.querySelector("#btn-cancel-student").addEventListener("click", () => {
         this.resetStudentDialog();
         this.studentDialog.close();
      });
      this.studentDialog.addEventListener("close", () => this.resetStudentDialog());
      this.querySelector("#new-student-form").addEventListener("submit", async (e) => this.onAddStudent(e));

      // Button: import student student list
      const dropzone = this.querySelector("#student-dropzone");
      const fileInput = this.querySelector("#student-list-file");

      dropzone.addEventListener("click", () => fileInput.click());
      dropzone.addEventListener("dragover", (e) => {
         e.preventDefault();
         dropzone.classList.add("dragover");
      });
      dropzone.addEventListener("dragleave", () => dropzone.classList.remove("dragover"));
      dropzone.addEventListener("drop", (e) => {
         e.preventDefault();
         dropzone.classList.remove("dragover");
         if (e.dataTransfer.files.length) {
            fileInput.files = e.dataTransfer.files;
            this.showPickedFile(fileInput.files[0]);
         }
      })
      fileInput.addEventListener("change", () => {
         if (fileInput.files.length) this.showPickedFile(fileInput.files[0]);
      });

      this.querySelector("#import-student-form").addEventListener("submit", async (e) => this.onImportStudents(e));
      this.querySelector("#btn-cancel-import").addEventListener("click", () => {
         this.resetStudentDialog();
         this.studentDialog.close(); 
      });

      // Button: add new assignment
      this.assignmentDialog = this.querySelector("#new-assignment-dialog");
      this.querySelector("#btn-new-assignment").addEventListener("click", async () => {
         this.querySelector("#new-assignment-form").reset();
         this.querySelector("#new-assignment-error").textContent = "";
         this.querySelector("#group-size-label").style.display = "none";
         // form.reset() restores the markup defaults (whole / untimed), so the
         // visibility just has to follow it.
         this._syncRunModeFields?.();
         this._editingId = null;
         this.querySelector("#new-assignment-dialog h2").textContent = "New assignment";
         this.querySelector("#btn-add-assignment").textContent = "Add to Class";
         // Fresh dialog: the next date the user picks should get the 11:59 PM default.
         this._duePristine = true;
         this._closePristine = true;
         // A NEW attachment has no window: blank means "available now, never
         // closes", which is how every assignment behaved before this existed.
         this.querySelector('[name="open_at"]').value = "";
         this.querySelector('[name="close_at"]').value = "";
         await this.loadAvailable();
         this.assignmentDialog.showModal();
         document.activeElement?.blur();
      });

      /*
      A due date means "by the end of that day", so picking a date should land on
      23:59, not the 00:00 a datetime-local gives you (which is the START of the day
      and quietly a day early).

      Only on the FIRST change after the field was empty, tracked by _duePristine.
      Blanket-rewriting every 00:00 would make midnight unselectable; this way the
      default is helpful and an explicit edit afterwards is still respected.
      */
      const dueInput = this.querySelector('[name="due_at"]');
      dueInput.addEventListener("change", () => {
         const v = dueInput.value;
         if (!v) { this._duePristine = true; return; }
         if (this._duePristine && v.endsWith("T00:00")) dueInput.value = v.slice(0, 11) + "23:59";
         this._duePristine = false;
      });

      /*
         CLOSES gets the same 11:59 PM treatment as the due date, and for the
         identical reason: "closes on the 26th" means the end of the 26th, and the
         browser's T00:00 would shut the assignment a full day early.

         OPENS deliberately does NOT get it. "Opens on the 26th" means from the
         start of the 26th, so T00:00 is already correct there and rewriting it
         would push the opening to the end of the day.
      */
      const closeInput = this.querySelector('[name="close_at"]');
      closeInput.addEventListener("change", () => {
         const v = closeInput.value;
         if (!v) { this._closePristine = true; return; }
         if (this._closePristine && v.endsWith("T00:00")) closeInput.value = v.slice(0, 11) + "23:59";
         this._closePristine = false;
      });

      // Show the target-group-size field only for a team formation method.
      /*
      Show the timing detail only when it means something.

      ⚠️ The database REFUSES timing_mode='async' with no duration
      (class_assignment_duration_chk), so a hidden-but-empty Minutes field would
      turn a legitimate save into a constraint error. Showing it is therefore not
      cosmetic: it is what makes the required field visible at the moment it
      becomes required. Same shape as the group-size toggle below.
      */
      const syncRunModeFields = () => {
         const timed = this.querySelector("#timing-mode").value === "async";
         this.querySelector("#duration-label").style.display = timed ? "block" : "none";
         this.querySelector("#attempts-label").style.display = timed ? "block" : "none";
      };
      this.querySelector("#timing-mode").addEventListener("change", syncRunModeFields);
      this._syncRunModeFields = syncRunModeFields;

      this.querySelector("#formation-method").addEventListener("change", (e) => {
         const isTeam = e.target.value && e.target.value !== "individual";
         this.querySelector("#group-size-label").style.display = isTeam ? "block" : "none";
      });

      this.querySelector("#btn-cancel-assignment").addEventListener("click", () => this.assignmentDialog.close());

      /*
      A save that ends in a WARNING keeps the dialog open, so the professor
      actually reads it rather than watching it flash past. But the save did
      happen, so the list behind is stale the moment it does.

      Hooked on the dialog's own `close` event rather than the Cancel button:
      Escape closes a <dialog> too, and hanging this off the button alone would
      leave the list stale for anyone who dismisses it that way.
      */
      this.assignmentDialog.addEventListener("close", async () => {
         if (!this._savedNeedsReload) return;
         this._savedNeedsReload = false;
         const reloadId = this.classId;
         this.classId = null;
         await this.show(reloadId);
      });
      this.querySelector("#new-assignment-form").addEventListener("submit", async (e) => this.onAddAssignment(e));
   }

   async show(classId) {
      if (this.classId === classId) return;
      this.classId = classId;
      await this.render();
   }

   async render() {
      this.querySelector("#cd-title").textContent = "Loading...";
      this.querySelector("#cd-sub").textContent = "";
      this.querySelector("#cd-roster").innerHTML = `<p class="empty">Loading...</p>`;
      this.querySelector("#cd-assignments").innerHTML = `<p class="empty">Loading...</p>`;

      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");

         const c = data.class;
         this.querySelector("#cd-title").textContent = `${c.course_code} · ${c.section}`;
         this.querySelector("#cd-sub").textContent = `${c.title || ""} — ${c.term}`;
         this.renderRoster(data.students || []);
         this.renderAssignments(data.assignments || [], data.library || null);
         this.renderVisibility(c);
         this.loadTargets();
      } catch {
         const el = this.querySelector("#cd-title");
         el.textContent = "Couldn't load this class.";
         el.classList.add("msg", "err");
      }
   }

   /*
      The two student-visibility switches.

      Each checkbox writes ONLY its own key (PATCH /classes/:id/visibility takes
      a partial body), so flipping one can never carry a stale value for the
      other back to the server - which is exactly what would happen if two
      professors had the page open and both posted the whole object.

      On failure the box is put back the way it was. A checkbox that stays ticked
      after a failed save is worse than no checkbox at all: it asserts the board
      is open when it is not.
   */
   renderVisibility(c) {
      const wrap = this.querySelector("#cd-visibility");
      wrap.hidden = false;

      const boxes = [
         ["#vis-scoreboard", "students_see_scoreboard"],
         ["#vis-engagement", "students_see_engagement"],
         // Not a visibility switch strictly - it gates a WRITE - but it rides the
         // same endpoint and the same partial-body discipline, and it is the same
         // question in the same place: what may the students of this class do.
         ["#vis-log-offline", "students_log_offline_time"],
         // 2026-10-15: the add-in's Game Launcher for this class (default off).
         ["#vis-game-launcher", "game_launcher"],
      ];
      for (const [sel, key] of boxes) {
         const box = this.querySelector(sel);
         box.checked = c[key] === true;
         if (box._wired) continue;
         box._wired = true;
         box.addEventListener("change", () => this.saveVisibility(box, key));
      }
   }

   /* Read through GET /engagement/settings rather than the class row, because
      that endpoint already owns the per-class engagement settings (time zone and
      default window) and degrades cleanly on a database that has not run the
      targets migration. */
   async loadTargets() {
      try {
         const res = await api(`/engagement/settings?class=${encodeURIComponent(this.classId)}`);
         const d = await res.json();
         if (!res.ok) return;
         const w = this.querySelector("#tgt-weekly");
         const c = this.querySelector("#tgt-cumulative");
         if (w) w.value = d.weeklyMinimumMinutes ?? "";
         if (c) c.value = d.cumulativeTargetMinutes ?? "";
         const btn = this.querySelector("#tgt-save");
         if (btn && !btn._wired) {
            btn._wired = true;
            btn.addEventListener("click", () => this.saveTargets());
         }
      } catch {
         /* Leave the fields blank. They are editable either way, and a failed
            read must not make the rest of the class page look broken. */
      }
   }

   async saveTargets() {
      const msg = this.querySelector("#cd-tgt-msg");
      const val = (sel) => {
         const raw = this.querySelector(sel).value.trim();
         return raw === "" ? null : Number(raw);
      };
      const weekly = val("#tgt-weekly");
      const cumulative = val("#tgt-cumulative");

      for (const [n, label] of [[weekly, "Weekly minimum"], [cumulative, "Term target"]]) {
         if (n !== null && (!Number.isInteger(n) || n <= 0)) {
            msg.textContent = `${label} must be a whole number of minutes, or blank for no target.`;
            msg.className = "msg err";
            return;
         }
      }

      msg.textContent = "Saving...";
      msg.className = "msg";
      try {
         /* Sends ONLY the two target keys. The endpoint is partial by design, so
            this cannot wipe the class's saved time zone or default window, which
            an earlier version of it would have done. */
         const res = await api("/engagement/settings", {
            method: "PUT",
            body: JSON.stringify({
               class: Number(this.classId),
               weeklyMinimumMinutes: weekly,
               cumulativeTargetMinutes: cumulative,
            }),
         });
         const d = await res.json();
         if (!res.ok) throw new Error(d.error || "failed");
         msg.textContent = weekly || cumulative
            ? "Saved. Students see these on their My time page."
            : "Saved. This class shows no target.";
         msg.className = "msg ok";
      } catch (err) {
         msg.textContent = String(err.message) === "migration_required"
            ? "The database is missing the targets migration. Run it, then try again."
            : "Could not save the targets.";
         msg.className = "msg err";
      }
   }

   async saveVisibility(box, key) {
      const msg = this.querySelector("#cd-vis-msg");
      const wanted = box.checked;
      box.disabled = true;
      msg.textContent = "Saving...";
      msg.className = "msg";
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/visibility`, {
            method: "PATCH",
            body: JSON.stringify({ [key]: wanted }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            throw new Error(data.reason || "failed");
         }
         box.checked = data.visibility[key] === true;
         msg.textContent = key === "game_launcher"
            ? (box.checked ? "Game Launcher on for this class." : "Game Launcher off for this class.")
            : (box.checked ? "Students can see this." : "Hidden from students.");
         msg.className = "msg ok";
      } catch (err) {
         box.checked = !wanted;   // revert; never leave a lie on screen
         /*
            Three distinct failures used to collapse into one useless sentence.

            `nothing_to_update` means the SERVER did not recognize the key this
            checkbox sent, which in practice means the portal (a static bundle on
            Cloudflare Pages) is ahead of the API (on Railway). That is not
            hypothetical: it happened on 2026-09-01 with this very checkbox,
            because `npm run build` compiles the working tree while Railway
            deploys from a git push, so the UI shipped and the route did not. The
            old message sent David looking at the database, which was fine.

            `migration_required` no longer names a single migration either: two
            of them now add columns this endpoint writes (2026-08-20, 2026-09-01).
         */
         const reason = String(err.message);
         msg.textContent =
            reason === "migration_required"
               ? "The database is missing a migration for this setting. Run the pending migrations, then try again."
            : reason === "nothing_to_update"
               ? "The server did not recognize this setting, so nothing was saved. It is usually running an older build than this page. Deploy the server, then try again."
               : "Couldn't save that. Nothing changed.";
         msg.className = "msg err";
      } finally {
         box.disabled = false;
      }
   }

   async loadAvailable() {
      const list = this.querySelector("#assignment-list");
      const hidden = this.querySelector("#assignment-id");
      hidden.value = "";
      list.innerHTML = `<p class="empty">Loading...</p>`;

      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/available-assignments`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         const items = data.assignments || [];
         if (!items.length) {
            list.innerHTML = `<p class="empty">No assignments available</p>`;
            return;
         }
         // CHECKBOXES, not single-select: attaching a term of exercises one dialog at
         // a time was the actual chore. A <label> wrapping the box makes the whole row
         // a hit target, so it still behaves like the old row button.
         list.classList.add("multi");
         list.innerHTML = items
            .map((a) => `
               <label class="pick-row" data-id="${a.assignment_id}" title="${esc(a.title || a.assignment_id)}">
                  <input type="checkbox" class="pick-check" value="${a.assignment_id}">
                  <span class="pick-name">${esc(a.title || a.assignment_id)}</span>
               </label>
            `).join("");
         list.querySelectorAll(".pick-check").forEach((box) => {
            box.addEventListener("change", () => {
               box.closest(".pick-row").classList.toggle("selected", box.checked);
               this.updateAssignmentSelection();
            });
         });
         this.updateAssignmentSelection();
      } catch {
         list.innerHTML = `<p class="empty">Couldn't load</p>`;
      }
   }

   // Which assignments are ticked. Edit mode never renders checkboxes, so it falls
   // back to the hidden field openEditAssignment sets — one id, as before.
   selectedAssignmentIds() {
      const boxes = [...this.querySelectorAll(".pick-check:checked")].map((b) => Number(b.value));
      if (boxes.length) return boxes;
      const one = Number(this.querySelector("#assignment-id").value);
      return Number.isInteger(one) && one > 0 ? [one] : [];
   }

   // Keep the button honest about how many rows it is about to create, and say once
   // that the Due and Team formation below are shared by all of them — otherwise
   // picking six and setting one date looks like it might only apply to the last.
   updateAssignmentSelection() {
      const n = this.querySelectorAll(".pick-check:checked").length;
      const hint = this.querySelector("#assignment-multi-hint");
      if (hint) {
         hint.hidden = n < 2;
         hint.textContent = `Due date and team formation apply to all ${n} selected.`;
      }
      if (this._editingId == null) {
         this.querySelector("#btn-add-assignment").textContent =
            n > 1 ? `Add ${n} to Class` : "Add to Class";
      }
   }

   renderRoster(students) {
      const el = this.querySelector("#cd-roster");
      if (!students.length) {
         el.innerHTML = `<p class="empty">No students enrolled yet.</p>`;
         return;
      }
      el.innerHTML = `
         <table class="cd-table">
            <thead>
               <tr><th>Name</th><th>Nickname</th><th>Email</th><th>Joined</th><th title="Exams only: scales this student's clock and the wave-2 time (1.5x: 60 minutes becomes 90, wave 2 at 45)">Exam time</th><th></th></tr>
            </thead>
            <tbody>
               ${students.map((s) => `
                  <tr>
                     <td>${esc(s.name)}</td>
                     <td>${esc(s.nickname || "—")}</td>
                     <td class="mono">${esc(s.email)}</td>
                     <td>${s.joined_at ? new Date(s.joined_at).toLocaleDateString() : "—"}</td>
                     <td>${this.examTimeSelect(s)}</td>
                     <td>
                        <button type="button" class="btn-icon btn-remove-student" data-id="${s.user_id}" data-email="${esc(s.email)}" aria-label="Remove" title="Remove">
                           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                           </svg>
                        </button>
                     </td>
                  </tr>
               `).join("")}
            </tbody>
         </table>
      `;

      el.querySelectorAll(".btn-remove-student").forEach((b) =>
         b.addEventListener("click", () => this.onRemoveStudent(b.dataset.id, b.dataset.email))
      );
      el.querySelectorAll(".cd-exam-time").forEach((sel) =>
         sel.addEventListener("change", () => this.onExamTime(sel))
      );
   }

   /* EXTRA TIME ON EXAMS (2026-10-16): standard, or 1.25x / 1.5x / 2x for every exam in this class. */
   examTimeSelect(s) {
      const cur = s.exam_time_factor != null ? Number(s.exam_time_factor) : 1;
      const opts = [[1, "Standard"], [1.25, "1.25x"], [1.5, "1.5x"], [2, "2x"]];
      if (!opts.some(([v]) => v === cur)) opts.push([cur, `${cur}x`]);
      return `<select class="cd-exam-time" data-id="${s.user_id}" data-name="${esc(s.name || s.email)}" data-cur="${cur}"
                 aria-label="Exam time for ${esc(s.name || s.email)}">
                 ${opts.map(([v, l]) => `<option value="${v}" ${v === cur ? "selected" : ""}>${l}</option>`).join("")}
              </select>`;
   }

   async onExamTime(sel) {
      const factor = Number(sel.value);
      sel.disabled = true;
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/enrollments/${encodeURIComponent(sel.dataset.id)}/exam-time`, {
            method: "PUT", body: JSON.stringify({ factor }) });
         const data = await res.json().catch(() => ({}));
         if (!res.ok || !data.ok) throw new Error(data.reason || String(res.status));
         sel.dataset.cur = String(factor);
         sel.title = factor > 1 ? `Saved: ${factor}x on every exam in this class` : "Saved: standard time";
      } catch (err) {
         sel.value = sel.dataset.cur;
         alert(String(err.message) === "migration_needed"
            ? "Extra time needs the 2026-10-16 exam-extras migration on the server first."
            : `Couldn't save the exam time for ${sel.dataset.name}.`);
      } finally {
         sel.disabled = false;
      }
   }

   async onRemoveStudent(userId, email) {
      if (!confirm(`Remove ${email} from this class?`)) return;

      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/enrollments/${encodeURIComponent(userId)}`, {
            method: "DELETE"
         });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("remove_failed");
         const reloadId = this.classId;
         this.classId = null;
         await this.show(reloadId);
      } catch {
         alert("Couldn't remove the student.");
      }
   }

   /*
      GAME LAUNCHER LIBRARY (2026-10-15, plan section 16, David's rulings 1-2).
      Library packs attached "launcher only" are folded into ONE block here instead
      of a card each (a whole library is about 140 packs): attach all / remove all,
      and a list with each pack's label (shown in the add-in's menu) and a Graded
      switch. Graded packs, and every other assignment, keep their normal cards.
   */
   renderLibrary(library, launcherOnly) {
      if (!library || !library.total) return "";
      const n = library.launcher_only || 0;
      const missing = library.total - (library.in_class || 0);
      return `
         <div class="cd-library">
            <div class="cd-library-head">
               <div>
                  <div class="cd-item-name">Game Launcher library</div>
                  <div class="cd-item-meta">
                     ${n} of ${library.total} library packs in the Game Launcher only (not graded, not on the Assignments tab).
                     Students pick them in the add-in, in any blank workbook.
                  </div>
               </div>
               <div class="cd-library-actions">
                  ${missing > 0 ? `<button type="button" class="btn-primary" id="btn-lib-attach">Attach all library packs${library.in_class ? ` (${missing} more)` : ""}</button>` : ""}
                  ${n > 0 ? `<button type="button" class="btn-secondary" id="btn-lib-detach">Remove launcher-only packs</button>` : ""}
               </div>
            </div>
            <div class="cd-library-data">
               How students did on this class's timed packs, library and graded (launcher packs are not in the gradebook):
               <a href="#" class="lnk-lib-data" data-kind="runs">Download runs (CSV)</a>
               <a href="#" class="lnk-lib-data" data-kind="tabs">Download tab detail (CSV)</a>
               <a href="#class-xp/${this.classId}" class="lnk-lib-xp">XP and skills: matrix, boards, squads</a>
               <a href="#class-readiness/${this.classId}" class="lnk-lib-xp" title="Each player against the skills their position needs (from the Skill map)">Team readiness: positions and skill gaps</a>
            </div>
            <p class="msg" id="lib-msg"></p>
            ${n ? `
            <details class="cd-library-list">
               <summary>Show the ${n} pack${n === 1 ? "" : "s"}: labels, grading, rules</summary>
               <table class="cd-table cd-library-table">
                  <thead><tr><th>Pack</th><th>Label in the menu</th><th>Graded</th><th></th></tr></thead>
                  <tbody>
                  ${launcherOnly.map((a) => `
                     <tr data-id="${a.assignment_id}">
                        <td>${esc(a.title || a.assignment_id)}</td>
                        <td><input type="text" class="lib-label" maxlength="60" placeholder="optional" value="${esc(a.pack_label || "")}"></td>
                        <td><input type="checkbox" class="lib-graded" title="Graded: gradebook column, Assignments card, grade records from now on"></td>
                        <td class="cd-pack-links"><a href="#pack-rules/${this.classId}/${a.assignment_id}">Rules</a><a href="#pack-rules/${this.classId}/${a.assignment_id}/board">Best times</a><a href="#pack-rules/${this.classId}/${a.assignment_id}/units" title="Each tab: question, worked formula, answer key; Blank and Solution downloads">Tabs</a></td>
                     </tr>`).join("")}
                  </tbody>
               </table>
            </details>` : ""}
         </div>`;
   }

   async libraryAction(action) {
      const msg = this.querySelector("#lib-msg");
      if (action === "detach" && !confirm("Remove every launcher-only library pack from this class? Students keep their times; the packs leave the Game Launcher menu. Graded packs stay.")) return;
      const btn = this.querySelector(action === "attach" ? "#btn-lib-attach" : "#btn-lib-detach");
      if (btn) btn.disabled = true;
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/library-packs`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action }) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         const reloadId = this.classId;
         this.classId = null;
         await this.show(reloadId);
      } catch (err) {
         if (msg) { msg.textContent = `Couldn't ${action === "attach" ? "attach" : "remove"} the library packs (${err.message}).`; msg.className = "msg err"; }
         if (btn) btn.disabled = false;
      }
   }

   async savePackLauncher(packId, body, row) {
      const msg = this.querySelector("#lib-msg") || this.querySelector("#cd-assignments");
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${packId}/launcher`, {
            method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         if (body.launcher_only !== undefined) {
            const reloadId = this.classId;
            this.classId = null;
            await this.show(reloadId);
            return;
         }
         if (row) { row.classList.add("saved"); setTimeout(() => row.classList.remove("saved"), 1200); }
      } catch (err) {
         if (msg && msg.id === "lib-msg") { msg.textContent = `Couldn't save (${err.message}).`; msg.className = "msg err"; }
         else alert(`Couldn't save (${err.message}).`);
      }
   }

   renderAssignments(assignments, library = null) {
      const el = this.querySelector("#cd-assignments");
      const launcherOnly = assignments.filter((a) => a.launcher_only);
      assignments = assignments.filter((a) => !a.launcher_only);
      const lib = this.renderLibrary(library, launcherOnly);
      if (!assignments.length) {
         el.innerHTML = `${lib}<p class="empty">No ${lib ? "graded " : ""}assignments yet.</p>`;
         this.wireLibrary(el);
         return;
      }
      el.innerHTML = `
         ${lib}
         <ul class="cd-list">
            ${assignments.map((a) => `
               <li class="cd-item">
                  <div class="cd-item-main">
                     <div class="cd-item-name">${esc(a.title || a.assignment_id)}</div>
                     <div class="cd-item-meta">
                        Due ${a.due_at ? new Date(a.due_at).toLocaleString() : "—"}
                        · ${a.submissions ?? 0} submissions
                        · ${a.formation_method === "student_select" ? "Teams (join code)" : a.formation_method ? esc(a.formation_method) : "Individual"}${modeLine(a)}
                     </div>
                     ${windowLine(a)}
                     ${a.archetype === "timed_pack" ? `
                     <div class="cd-pack-links">
                        <a href="#pack-rules/${this.classId}/${a.assignment_id}">Rules for this class</a>
                        <a href="#pack-rules/${this.classId}/${a.assignment_id}/board">Best times</a>
                        <a href="#pack-rules/${this.classId}/${a.assignment_id}/units" title="Each tab: question, worked formula, answer key; Blank and Solution downloads">Tabs and solutions</a>
                        <a href="#pack-rules/${this.classId}/${a.assignment_id}/exam" title="In-class exam: open it, release wave 2, end it, and watch the live board">Exam</a>
                        <a href="#" class="lnk-pack-label" data-id="${a.assignment_id}" title="The label students see beside this pack in the add-in's Game Launcher">${a.pack_label ? `Launcher label: ${esc(a.pack_label)}` : "Launcher label"}</a>
                        ${a.in_library ? `<a href="#" class="lnk-pack-ungrade" data-id="${a.assignment_id}" title="Back into the Game Launcher library box: no gradebook column or card; grades already written stay">Make launcher only</a>` : ""}
                     </div>` : ""}
                  </div>
                  <div class="cd-item-actions">
                     <button type="button" class="btn-icon btn-edit-assignment" data-id="${a.assignment_id}" aria-label="Edit" title="Edit">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                           <path d="M12 20h9"/>
                           <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                        </svg>
                     </button>
                     <button type="button" class="btn-icon btn-delete-assignment" data-id="${a.assignment_id}" data-submissions="${a.submissions ?? 0}" aria-label="Delete" title="Delete">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                           <polyline points="3 6 5 6 21 6"/>
                           <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                           <line x1="10" y1="11" x2="10" y2="17"/>
                           <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                     </button>
                  </div>
               </li>
            `).join("")}
         </ul>
      `;
      this._assignments = assignments;
      this.wireLibrary(el);
      el.querySelectorAll(".lnk-pack-ungrade").forEach((a) => a.addEventListener("click", (e) => {
         e.preventDefault();
         if (!confirm("Make this pack launcher only? Its gradebook column and Assignments card go away, and finished runs stop writing grades. Grades already written are kept in the database.")) return;
         this.savePackLauncher(Number(a.dataset.id), { launcher_only: true }, null);
      }));
      el.querySelectorAll(".lnk-pack-label").forEach((a) => a.addEventListener("click", (e) => {
         e.preventDefault();
         const cur = (this._assignments.find((x) => x.assignment_id === Number(a.dataset.id)) || {}).pack_label || "";
         const v = prompt("Label students see beside this pack in the Game Launcher (blank to clear, 60 characters at most):", cur);
         if (v === null) return;
         this.savePackLauncher(Number(a.dataset.id), { label: v }, null).then(() => {
            const reloadId = this.classId; this.classId = null; this.show(reloadId);
         });
      }));

      el.querySelectorAll(".btn-edit-assignment").forEach((b) =>
         b.addEventListener("click", () => this.openEditAssignment(Number(b.dataset.id)))
      );
      el.querySelectorAll(".btn-delete-assignment").forEach((b) =>
         b.addEventListener("click", () => this.onDeleteAssignment(Number(b.dataset.id), Number(b.dataset.submissions)))
      );
   }

   wireLibrary(el) {
      // Download via a normal navigation (the route sets Content-Disposition; the session
      // cookie rides along), the same way the grades export does.
      el.querySelectorAll(".lnk-lib-data").forEach((a) => a.addEventListener("click", (e) => {
         e.preventDefault();
         const base = (location.hostname === "localhost") ? "https://localhost:3001" : "https://api.gridprof.com";
         window.location.href = `${base}/classes/${encodeURIComponent(this.classId)}/launcher-activity?kind=${a.dataset.kind}&format=csv`;
      }));
      el.querySelector("#btn-lib-attach")?.addEventListener("click", () => this.libraryAction("attach"));
      el.querySelector("#btn-lib-detach")?.addEventListener("click", () => this.libraryAction("detach"));
      el.querySelectorAll(".cd-library-table tr[data-id]").forEach((row) => {
         const id = Number(row.dataset.id);
         const label = row.querySelector(".lib-label");
         label?.addEventListener("change", () => this.savePackLauncher(id, { label: label.value }, row));
         const graded = row.querySelector(".lib-graded");
         graded?.addEventListener("change", () => {
            if (graded.checked && !confirm("Make this pack graded in this class? It gets a gradebook column and an Assignments card, and finished timed runs write grades from now on (set its thresholds on the Rules screen).")) {
               graded.checked = false; return;
            }
            this.savePackLauncher(id, { launcher_only: !graded.checked }, row);
         });
      });
   }

   /*
      Pull every email-shaped token out of pasted text. Same rule the server-side
      bulk CLI uses, so pasting a roster column behaves identically in both places:
      separators (new lines, commas, semicolons, spaces) and non-email text are
      ignored, addresses are lowercased, and duplicates collapse.
   */
   extractEmails(text) {
      const seen = new Set();
      const out = [];
      const matches = String(text || "").match(/[^\s,;<>"']+@[^\s,;<>"']+\.[^\s,;<>"']+/g) || [];
      for (const raw of matches) {
         const email = raw.trim().toLowerCase().replace(/[.,;]+$/, "");
         if (seen.has(email)) continue;
         seen.add(email);
         out.push(email);
      }
      return out;
   }

   // One phrasing of the enrollment result, shared by the paste and import paths.
   summarizeEnrollResult(data) {
      const parts = [];
      if (data.added?.length) parts.push(`${data.added.length} added`);
      if (data.created?.length) parts.push(`${data.created.length} new account${data.created.length === 1 ? "" : "s"}`);
      if (data.already?.length) parts.push(`${data.already.length} already enrolled`);
      if (data.invalid?.length) parts.push(`${data.invalid.length} invalid`);
      return parts.join(" · ");
   }

   async onAddStudent(e) {
      e.preventDefault();
      const btn = this.querySelector("#btn-add-student");
      const msg = this.querySelector("#add-student-msg");
      const field = this.querySelector('#new-student-form [name="email"]');
      msg.textContent = "";
      msg.className = "msg";

      // Accepts one address or a whole pasted block.
      const emails = this.extractEmails(field.value);
      if (!emails.length) {
         this.setMsg(msg, "No email addresses found.", { error: true, clearAfter: 3000 });
         return;
      }

      btn.disabled = true;
      btn.textContent = emails.length > 1 ? `Adding ${emails.length}...` : "Adding...";
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/enrollments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ emails })
         });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "add_student_failed");

         const summary = this.summarizeEnrollResult(data);

         if (data.added?.length) {
            // Show what happened before closing, so a mixed batch isn't silent.
            this.setMsg(msg, summary, { ok: true });
            setTimeout(async () => {
               this.studentDialog.close();
               const reloadId = this.classId;
               this.classId = null;
               await this.show(reloadId);
            }, 1200);
         } else if (data.already?.length && !data.invalid?.length) {
            this.setMsg(msg, emails.length > 1
               ? "Everyone on that list is already enrolled."
               : "This student is already enrolled.", { error: true, clearAfter: 4000 });
         } else {
            this.setMsg(msg, summary || "Couldn't add anyone from that list.", { error: true, clearAfter: 4000 });
         }
      } catch {
         this.setMsg(msg, "Couldn't add the students. Try again.", { error: true, clearAfter: 3000 });
      } finally {
         btn.disabled = false;
         btn.textContent = "Add";
      }
   }

   async onImportStudents(e) {
      e.preventDefault();
      const btn = this.querySelector("#btn-import");
      const msg = this.querySelector("#import-msg");
      if (!msg) return;
      const fileInput = this.querySelector("#student-list-file");
      msg.className = "msg";

      if (!fileInput.files.length) {
         this.setMsg(msg, "Pick a file first.", { error: true, clearAfter: 3000 });
         return;
      }

      btn.disabled = true;
      btn.textContent = "Importing...";
      try {
         const emails = await this.parseEmailsFromFile(fileInput.files[0]);
         if (!emails.length) {
            this.setMsg(msg, "No emails found in that file.", { error: true });
            return;
         }

         const res = await api(`/classes/${encodeURIComponent(this.classId)}/enrollments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ emails })
         });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "import_failed");

         this.setMsg(msg, this.summarizeEnrollResult(data) || "Done.", { ok: true });

         if (data.added?.length) {
            setTimeout(async () => {
               this.studentDialog.close();
               const reloadId = this.classId;
               this.classId = null;
               await this.show(reloadId);
            }, 1200);
         }
      } catch (err) {
         this.setMsg(msg, err.message === "xlsx_not_loaded"
            ? "Excel support isn't loaded. Use a CSV, or add the SheetJS library."
            : "Couldn't import that file.", { error: true, clearAfter: 3000});
      } finally {
         btn.disabled = false;
         btn.textContent = "Import";
      }
   }

   async parseEmailsFromFile(file) {
      const name = file.name.toLowerCase();
      let rows = [];

      if (name.endsWith(".csv")) {
         const text = await file.text();
         rows = text.split(/\r?\n/).map((line) => line.split(","));
      } else if (/\.xls[xm]?$/.test(name)) {
         const XLSX = window.XLSX;
         if (!XLSX) throw new Error("xlsx_not_loaded");
         const buf = await file.arrayBuffer();
         const wb = XLSX.read(buf, { type: "array" });
         const sheet = wb.Sheets[wb.SheetNames[0]];
         rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      } else {
         throw new Error("unsupported_file");
      }

      if (!rows.length) return [];

      // Find an "email" column
      const header = rows[0].map((c) => String(c ?? "").trim().toLowerCase());
      const emailIdx = header.indexOf("email");
      const startRow = emailIdx === -1 ? 0 : 1;
      const col = emailIdx === -1 ? 0 : emailIdx;

      return rows
         .slice(startRow)
         .map((r) => String(r[col] ?? "").trim())
         .filter((v) => v.includes("@"));
   }

   showPickedFile(file) {
      const name = file.name.length > 30
         ? file.name.slice(0, 18) + "..." + file.name.slice(-9)
         : file.name;
      const dropzone = this.querySelector("#student-dropzone");
      const dzText = dropzone.querySelector(".dropzone-text");
      if (dzText) dzText.textContent = `Selected: ${name}`;
      dropzone.classList.add("has-file");
      const msg = this.querySelector("#import-msg");
      msg.className = "msg";
      msg.textContent = "";
   }

   resetStudentDialog() {
      this.querySelector("#new-student-form").reset();
      this.querySelector("#import-student-form").reset();
      const fileInput = this.querySelector("#student-list-file");
      fileInput.value = "";
      const dropzone = this.querySelector("#student-dropzone");
      if (dropzone) {
         dropzone.classList.remove("has-file");
         const t = dropzone.querySelector(".dropzone-text");
         if (t) t.innerHTML = `Drop a CSV or Excel file, or <span class="link">browse</span>`;
      }
      ["#add-student-msg", "#import-msg"].forEach((sel) => {
         const m = this.querySelector(sel);
         if (m) {
            m.textContent = "";
            m.className = "msg";
         }
      });
      clearTimeout(this._msgTimer);
   }

   async onAddAssignment(e) {
      e.preventDefault();
      const err = this.querySelector("#new-assignment-error");
      const ids = this.selectedAssignmentIds();
      if (!ids.length) {
         this.setMsg(err, "Pick an assignment.", { error: true, clearAfter: 3000 });
         return;
      }

      const editing = this._editingId != null;
      const url = editing
         ? `/classes/${encodeURIComponent(this.classId)}/assignments/${this._editingId}`
         : `/classes/${encodeURIComponent(this.classId)}/assignments`;

      try {
         const res = await api(url, {
            method: editing ? "PATCH" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               // Editing is always exactly one attachment; adding may be several, and
               // the server puts the whole batch in one transaction.
               ...(editing ? { assignment_id: ids[0] } : { assignment_ids: ids }),
               // localToIso, not .value - see the comment on localToIso. Sending
               // the bare control value is what made every time land 7 hours early.
               due_at: localToIso(this.querySelector('[name="due_at"]').value),
               open_at: localToIso(this.querySelector('[name="open_at"]').value),
               close_at: localToIso(this.querySelector('[name="close_at"]').value),
               formation_method: this.querySelector('[name="formation_method"]').value || "individual",
               target_group_size: Number(this.querySelector('[name="target_group_size"]').value) || 3,
               // MINUTES on the wire; the server converts to seconds. Sending
               // seconds from here would put the same conversion in two places.
               delivery_mode: this.querySelector('[name="delivery_mode"]').value || "whole",
               timing_mode: this.querySelector('[name="timing_mode"]').value || "untimed",
               duration_mins: this.querySelector('[name="duration_mins"]').value,
               // "" means unlimited and the server reads it that way. Number("")
               // is 0, which the CHECK would reject, so this stays a string.
               max_attempts: this.querySelector('[name="max_attempts"]').value,
            }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            /*
            ⚠️ REPORT THE SERVER'S REASON, not a generic failure. readRunModes
            answers with a specific one for every case the database would refuse,
            and "Couldn't save changes" for a missing duration would send a
            professor hunting through a form that is telling them nothing.
            */
            const why = {
               duration_required: "Enter how many minutes the timed assignment should last.",
               invalid_max_attempts: "Attempts must be a whole number, or blank for unlimited.",
               invalid_delivery_mode: "That delivery option is not recognised.",
               invalid_timing_mode: "That timing option is not recognised.",
               close_before_open: "The close time must be after the open time.",
            }[data && data.reason];
            if (why) {
               this.setMsg(err, why, { error: true, clearAfter: 6000 });
               return;
            }
            throw new Error("add_assignment_failed");
         }

         /*
         ⚠️ A SAVED SETTING THAT CANNOT WORK IS WORSE THAN A REFUSED ONE, because
         it looks done. The server derives the shell at save time and reports why
         it could not, so the professor learns here rather than from a student's
         download answering 503. Nine builds in the library carry VBA a worksheet
         insert cannot carry, and three have no AddInInformation at all.

         Saved anyway, deliberately: the setting is legitimate and the file may
         be re-uploaded fixed. This warns; it does not undo.
         */
         if (Array.isArray(data.warnings) && data.warnings.length) {
            const first = data.warnings[0];
            this.setMsg(err,
               `Saved, but this assignment cannot be delivered as a cover page yet: ` +
               `${first.reason}. Students would get an error instead of a file.`,
               { error: true });
            // The write committed; the list behind this dialog is now stale.
            // Refreshed when they dismiss, see the close handler.
            this._savedNeedsReload = true;
            return;
         }

         this.assignmentDialog.close();
         const reloadId = this.classId;
         this.classId = null;
         await this.show(reloadId);
      } catch (e) {
         console.error("assignment save failed:", e);
         this.setMsg(err, editing ? "Couldn't save changes." : "Couldn't add assignment.", { error: true, clearAfter: 3000 });
      } finally {
         this._editingId = null;
      }
   }
   
   openEditAssignment(assignmentId) {
      const a = (this._assignments || []).find((x) => x.assignment_id === assignmentId);
      if (!a) return;

      this._editingId = assignmentId;

      // Hide the picker + show the title as read-only; editing doesn't change which assignment it is
      const list = this.querySelector("#assignment-list");
      list.classList.remove("multi");   // may be left over from a previous Add
      list.innerHTML = `<p class="pick-static">${esc(a.title || a.assignment_id)}</p>`;
      this.querySelector("#assignment-id").value = assignmentId;
      this.querySelector("#assignment-multi-hint").hidden = true;

      // Prefill due date - datetime-local wants "YYYY-MM-DDTHH:mm" in local time
      const dueInput = this.querySelector('[name="due_at"]');
      dueInput.value = a.due_at ? toLocalInput(a.due_at) : "";
      // An existing due date is the user's own choice, so leave it alone; an empty one
      // should still get the 11:59 PM default when they pick a date.
      this._duePristine = !a.due_at;

      /*
         Opens/closes prefill the same way. The TIME DEFAULTS differ on purpose,
         and it is the same trap the due date already hit: a `datetime-local`
         picked by date alone returns T00:00.

           Opens  -> 00:00 is RIGHT. "Opens on the 26th" means from the start of
                     the 26th, so the browser's own default already means what
                     the professor meant.
           Closes -> 00:00 is WRONG, and badly so: "closes on the 26th" means the
                     end of the 26th, and midnight would shut it a full day early.
                     Defaulted to 11:59 PM, exactly like the due date.
      */
      const openInput = this.querySelector('[name="open_at"]');
      openInput.value = a.open_at ? toLocalInput(a.open_at) : "";
      const closeInput = this.querySelector('[name="close_at"]');
      closeInput.value = a.close_at ? toLocalInput(a.close_at) : "";
      this._closePristine = !a.close_at;

      // Prefill formation
      const fm = this.querySelector('[name="formation_method"]');
      fm.value = a.formation_method || "individual";
      const isTeam = fm.value && fm.value !== "individual";
      this.querySelector("#group-size-label").style.display = isTeam ? "block" : "none";
      if (a.target_group_size) this.querySelector('[name="target_group_size"]').value = a.target_group_size;

      // Delivery + timing. Seconds come back from the server; the field is in
      // minutes, so convert on the way in exactly as the save converts on the
      // way out.
      this.querySelector('[name="delivery_mode"]').value = a.delivery_mode || "whole";
      this.querySelector('[name="timing_mode"]').value = a.timing_mode || "untimed";
      this.querySelector('[name="duration_mins"]').value =
         a.duration_secs ? Math.round(a.duration_secs / 60) : 30;
      this.querySelector('[name="max_attempts"]').value =
         a.max_attempts == null ? "" : a.max_attempts;
      this._syncRunModeFields?.();

      this.querySelector("#new-assignment-dialog h2").textContent = "Edit assignment";
      this.querySelector("#btn-add-assignment").textContent = "Save";
      this.querySelector("#new-assignment-error").textContent = "";
      this.assignmentDialog.showModal();
      document.activeElement?.blur();
   }

   async onDeleteAssignment(assignmentId, submissions) {
      if (submissions > 0) {
         alert(`This assignment has ${submissions} submission(s) and can't be removed.`);
         return;
      }

      const a = (this._assignments || []).find((x) => x.assignment_id === assignmentId);
      const label = a?.title || assignmentId;
      if (!confirm(`Remove "${label}" from this class? The assignment file itself is kept.`)) return;

      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/assignments/${assignmentId}`, {
            method: "DELETE",
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            if (data.reason === "has_submissions") {
               alert("This assignment has submissions and can't be removed.");
               return;
            }
            throw new Error("delete failed");
         }
         const reloadId = this.classId;
         this.classId = null;
         await this.show(reloadId);
      } catch {
         alert("Couldn't remove the assignment.");
      }
   }

   setMsg(el, text, { error = false, ok = false, clearAfter = 0 } = {}) {
      if (!el) return;
      el.textContent = text;
      el.className = error ? "msg err" : ok ? "msg ok" : "msg";
      clearTimeout(this._msgTimer);
      if (clearAfter > 0) {
         this._msgTimer = setTimeout(() => {
            el.textContent = "";
            el.className = "msg";
         }, clearAfter);
      }
   }
}

customElements.define("app-class-detail", AppClassDetail);
